import { useMutation } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/outline";
import { ClickEvent, MenuItem, MenuItemProps } from "@szhsin/react-menu";
import { Editor } from "@tiptap/core";

import { errorNotificationVar } from "@cache/cache";
import removeTopicFromCache from "@cache/remove-topic";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { classNames } from "@helpers/css";

import deleteTopicMutation from "../graphql/delete-topic-mutation";

const DeleteMenuItem = ({
  topic,
  meeting,
  onDelete,
  disabled,
  ...props
}: MenuItemProps & {
  topic: any;
  meeting: any;
  onDelete: () => void;
}) => {
  /* Mutations */
  const [deleteTopic] = useMutation(deleteTopicMutation, {
    onCompleted: () => {
      onDelete();
    },
    onError: () => {
      errorNotificationVar({
        description: "The topic could not be deleted.",
      });
    },
    update: () => {
      removeTopicFromCache({
        topic,
        meeting,
      });
    },
  });

  /* Handler */
  const handleDeleteTopic = (e: ClickEvent) => {
    const editorEl = document.querySelector(
      `#editor-topic-${topic.id} .js-topic-discussion-notes-input`,
    ) as Element & { editor: Editor };
    const isEmpty = editorEl && isEmptyValue(editorEl.editor.getJSON());
    // eslint-disable-next-line no-restricted-globals
    if (isEmpty || confirm("Are you sure you want to delete this topic?")) {
      deleteTopic({
        variables: { topicId: topic.id },
        optimisticResponse: {
          deleteTopic: {
            ok: true,
          },
        },
      });
      e.stopPropagation = true;
    }
  };

  // Render
  const menuItemClassName = "text-sm ";
  return (
    <MenuItem
      className={menuItemClassName}
      onClick={handleDeleteTopic}
      data-testid="topic-dropdown-delete"
      disabled={disabled}
      {...props}
    >
      <TrashIcon
        className={classNames(
          "mr-3 h-5 w-5",
          disabled
            ? "text-gray-300"
            : "text-gray-400 group-hover:text-gray-500",
        )}
      />{" "}
      Delete
    </MenuItem>
  );
};

export default DeleteMenuItem;
